import { QueryClient } from "@tanstack/react-query";
import { BaseLogo, ProductRef } from "../../core/models/entities/BaseLogo";
import { PagedResponse } from "../models/responses/pagedResponse";
import { actAsCacheKey } from "../../contexts/actAsStore";
import { enviroment } from "../../enviroment";

// Kept out of LogoRepository on purpose: that module imports types from
// useLogoActionsHandler, and the actions handler needs these helpers - a
// separate leaf module avoids an import cycle.

// Detail cache key under the CURRENT act-as scope - the exact shape
// LogoRepository.useLogo reads (["logos", actAs, id]).
export function logoDetailKey(logoId: BaseLogo["id"]): (string | number)[] {
  return ["logos", actAsCacheKey(), logoId];
}

// A detail entry: ["logos", actAs, id] (LogoRepository.useLogo). Every other
// "logos" key is length 4 (list: ["logos", actAs, typeFilter, qs]; infinite:
// ["logos", "inf", actAs, qs]; design picker: ["logos", customerId, "inf",
// qs]) or the bare ["logos"] prefix, so length-3-with-two-numbers uniquely
// identifies a detail entry. ONE definition, shared by every predicate that
// must recognize (or exclude) detail entries - the old hand-rolled
// `length === 2 && typeof key[1] === "number"` checks silently stopped
// matching when the act-as segment entered the key. Lives in this leaf
// module (re-exported by LogoRepository for its historical importers) so the
// cache patch below can use it without an import cycle.
export function isLogoDetailQueryKey(key: readonly unknown[]): boolean {
  return (
    key[0] === "logos" &&
    key.length === 3 &&
    typeof key[1] === "number" &&
    typeof key[2] === "number"
  );
}

/**
 * The act-as segment of a ["logos", ...] query key - the scope whose rows
 * the entry holds: ["logos", actAs, id] (detail), ["logos", actAs,
 * typeFilter, qs] (paged list) and ["logos", "inf", actAs, qs] (infinite
 * list). Undefined for every other form - the design picker's ["logos",
 * customerId, "inf", qs] carries no act-as segment, so which organization's
 * rows it holds cannot be told from the key. Re-exported by
 * utils/functions/storageFreshness for its historical importers.
 */
export function logoQueryKeyScope(key: readonly unknown[]): number | undefined {
  if (key[0] !== "logos") return undefined;
  if (isLogoDetailQueryKey(key)) return key[1] as number;
  if (key.length !== 4) return undefined;
  if (key[1] === "inf") {
    return typeof key[2] === "number" ? key[2] : undefined;
  }
  if (typeof key[1] === "number" && key[2] !== "inf") return key[1];
  return undefined;
}

// Explicit "no genuine high-res yet" mark on a cached detail entry (flag
// REACT_APP_LOGO_HEAL_PER_MUTATION). The copy response carries the 180px
// processed blob AS highResUrl (a data-URI that differs from lowResUrl), so
// the heal's equality rule never sees a clone as pending; the clone flows
// stamp the seeded entry instead and isHighResPending honours the field.
// No-op when the entry does not exist (never creates a fabricated entry).
export function markDetailHighResPending(
  queryClient: QueryClient,
  logoId: BaseLogo["id"],
): void {
  const key = logoDetailKey(logoId);
  const current = queryClient.getQueryData<BaseLogo>(key);
  if (!current || !current.displayImage) return;
  if (current.displayImage.highResPending === true) return;
  queryClient.setQueryData<BaseLogo>(key, {
    ...current,
    displayImage: { ...current.displayImage, highResPending: true },
  });
}

// Flag REACT_APP_LOGO_HEAL_PER_MUTATION: drop a heal poll (GetLogo) that is
// still in flight for this logo right before a mutation response is seeded.
// Without this a poll started BEFORE the mutation can land AFTER its
// response, overwrite the seeded entry with the pre-mutation render and -
// because that render may carry a genuine high-res - stop the heal, so the
// synced canvas would show the old colours/size until F5. react-query
// ignores the result of a cancelled fetch and reverts the entry to its
// pre-fetch state, which the seed then replaces. No-op when nothing is in
// flight; a no-op with the flag off (the canvas is not synced then).
// Flag REACT_APP_PRODUCT_PENDING_ENABLED needs the same guard on its own:
// its 3/8/15 s re-read of this entry (useProductPendingPoll) started before
// a mutation would otherwise land after the response with the PRE-mutation
// product (productPending=false, old prices) - badge gone, old price shown
// until the next poll. Flag REACT_APP_CLONE_INSTANT_ENABLED likewise: its
// "Preparing logo…" re-read (useEditorUploadPendingPoll) must not land a
// pre-mutation detail over the seeded response when it runs alone (heal and
// product flags off). All three flags off = the no-op as before.
export function cancelInFlightDetailFetch(
  queryClient: QueryClient,
  logoId: BaseLogo["id"],
): void {
  if (
    !enviroment.logoHealPerMutationEnabled &&
    !enviroment.productPendingEnabled &&
    !enviroment.cloneInstantEnabled
  ) {
    return;
  }
  void queryClient.cancelQueries({
    queryKey: logoDetailKey(logoId),
    exact: true,
  });
}

// Flag REACT_APP_LOGO_POPUP_FAST: seed the detail entry with the LIST row the
// popup was opened from, so useLogo has data on its very first render and the
// popup paints the row's image, prices, colours and stock at once. Only when
// the entry is EMPTY or still a STUB from an earlier open - a cached GetLogo
// (earlier open, deep-link seed, mutation seed) is always better than a row
// and is never overwritten. The stub is stamped detailFromList (see BaseLogo)
// and written with updatedAt 0, i.e. stale from birth; the caller fires the
// ONE background GetLogo that replaces it (refetchOnMount is off globally, so
// nothing else would). The zero stamp also means "never loaded" to
// react-query, so a stub left behind by a popup closed before its GetLogo
// landed is loaded on the next mount anyway. A stub found here is seeded
// AGAIN rather than kept: that resets it to updatedAt 0 and status success
// whatever happened to it meanwhile (a failed GetLogo left it in error; a
// writer that stamped it fresh would otherwise make the mount skip the load)
// - so it can never stick. Returns whether a seed was written. Flag off =
// no-op, nothing touched.
export function seedLogoDetailFromList(
  queryClient: QueryClient,
  logo: BaseLogo,
): boolean {
  if (!enviroment.logoPopupFastEnabled) return false;
  const key = logoDetailKey(logo.id);
  const current = queryClient.getQueryData<BaseLogo>(key);
  if (current !== undefined && !isLogoDetailFromList(current)) return false;
  queryClient.setQueryData<BaseLogo>(
    key,
    { ...logo, detailFromList: true },
    { updatedAt: 0 },
  );
  return true;
}

// True while the detail entry is still the list-row stub above (the GetLogo
// response never carries the mark). Consumers that must judge the REAL
// detail (heal poll, "prices on their way" poll, View designs) pass
// undefined instead of a stub.
export function isLogoDetailFromList(logo: BaseLogo | undefined): boolean {
  return logo?.detailFromList === true;
}

// ---------------------------------------------------------------------------
// "Prices on their way" / "Preparing logo…" in the LISTS (flags
// REACT_APP_PRODUCT_PENDING_ENABLED / REACT_APP_CLONE_INSTANT_ENABLED).
//
// The list entries (["logos", actAs, typeFilter, qs], staleTime 60 s,
// refetchOnMount off) are only ever rewritten by a query change, a mutation
// invalidation or the return-to-list refetch - and that refetch races the
// API worker: a resize stamps productPending, the user goes back to /logo,
// the list is refetched at once (worker not done -> row pending) and nothing
// afterwards writes a GetLogo answer back into the row, so the badge stays
// until F5 (prod 675889, 11/9). Every pending re-read (popup poll, editor
// heal/poll, cart sync, the list poll) lands in the DETAIL entry; this
// helper copies the landed pending state (and the worker's product/prices)
// from that detail into the row with the same id in every list entry of the
// same act-as scope. 0 calls: pure cache write.

function isPagedLogos(data: unknown): data is PagedResponse<BaseLogo> {
  return (
    !!data &&
    typeof data === "object" &&
    Array.isArray((data as PagedResponse<BaseLogo>).results)
  );
}

function isInfiniteLogos(
  data: unknown,
): data is { pages: PagedResponse<BaseLogo>[] } {
  return (
    !!data &&
    typeof data === "object" &&
    Array.isArray((data as { pages?: unknown }).pages)
  );
}

function sameProduct(a: ProductRef | undefined, b: ProductRef | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.sku === b.sku &&
    JSON.stringify(a.prices ?? null) === JSON.stringify(b.prices ?? null) &&
    JSON.stringify(a.additionalFees ?? null) ===
      JSON.stringify(b.additionalFees ?? null)
  );
}

// A row is only touched while it - or the detail - is pending under an
// ENABLED variant (the flag-gated predicates in productPending.ts are the
// same rule the badge renders on), and only the fields of the enabled
// variants plus the product are written. Rows that are not pending on either
// side keep their identity even when the detail carries newer prices: this
// patch is about the pending state, not a general "detail wins" sync. A row
// with nothing to change is returned AS IS (reference check = no dispatch).
function patchRowFromDetail(row: BaseLogo, detail: BaseLogo): BaseLogo {
  if (!row || row.id !== detail.id) return row;
  const productEnabled = enviroment.productPendingEnabled;
  const uploadEnabled = enviroment.cloneInstantEnabled;
  const rowPending =
    (productEnabled && row.productPending === true) ||
    (uploadEnabled && row.uploadPending === true);
  const detailPending =
    (productEnabled && detail.productPending === true) ||
    (uploadEnabled && detail.uploadPending === true);
  if (!rowPending && !detailPending) return row;

  const productSame =
    !productEnabled ||
    ((row.productPending === true) === (detail.productPending === true) &&
      (row.productPendingSinceUtc ?? null) ===
        (detail.productPendingSinceUtc ?? null));
  const uploadSame =
    !uploadEnabled ||
    ((row.uploadPending === true) === (detail.uploadPending === true) &&
      (row.uploadPendingSinceUtc ?? null) ===
        (detail.uploadPendingSinceUtc ?? null));
  if (productSame && uploadSame && sameProduct(row.product, detail.product)) {
    return row;
  }
  const next: BaseLogo = { ...row, product: detail.product };
  if (productEnabled) {
    next.productPending = detail.productPending;
    next.productPendingSinceUtc = detail.productPendingSinceUtc;
  }
  if (uploadEnabled) {
    next.uploadPending = detail.uploadPending;
    next.uploadPendingSinceUtc = detail.uploadPendingSinceUtc;
  }
  return next;
}

function patchResultsFromDetail(
  results: BaseLogo[] | undefined,
  detail: BaseLogo,
): BaseLogo[] | undefined {
  if (!Array.isArray(results)) return results;
  let changed = false;
  const next = results.map((row) => {
    const patched = patchRowFromDetail(row, detail);
    if (patched !== row) changed = true;
    return patched;
  });
  return changed ? next : results;
}

/**
 * A cached list entry with the detail's pending state applied to the row of
 * the same id: a paged list ({results}) or an infinite list ({pages:
 * [{results}]}). Any other shape, or nothing to change, comes back as the
 * very same reference. Exported for tests.
 */
export function patchCachedLogoRowsData(data: unknown, detail: BaseLogo): unknown {
  if (!data) return data;
  if (isPagedLogos(data)) {
    const results = patchResultsFromDetail(data.results, detail);
    return results === data.results ? data : { ...data, results };
  }
  if (isInfiniteLogos(data)) {
    let changed = false;
    const pages = data.pages.map((page) => {
      if (!isPagedLogos(page)) return page;
      const results = patchResultsFromDetail(page.results, detail);
      if (results === page.results) return page;
      changed = true;
      return { ...page, results };
    });
    return changed ? { ...data, pages } : data;
  }
  return data;
}

/**
 * Copies the pending state a landed GetLogo (`detail`) carries into the row
 * with the same id in every LIST entry under ["logos"] that belongs to the
 * current act-as scope (same rule as patchStorageInQueryCache): the paged
 * lists (logo list screen) and the infinite lists (design editor picker).
 * Written per row: productPending / productPendingSinceUtc (product flag),
 * uploadPending / uploadPendingSinceUtc (clone flag) and `product` (the
 * worker's sku/prices) - never the whole detail (its high-res data-URIs
 * must not grow the list), never a row that is pending on neither side.
 *
 * Never written: detail entries (the caller's own entry included), another
 * act-as scope's lists (a previous vendor's rows, still cached, must not get
 * this scope's prices), the design picker's unscoped entry, and a list that
 * is NEWER than the detail entry (dataUpdatedAt): a cached GetLogo from
 * minutes ago cannot clear a badge the list was told about since. The
 * popup's list-row stub (isLogoDetailFromList) is not a detail and patches
 * nothing. Entries that would not change are not written at all - no
 * dispatch, no re-render. Returns the number of entries written. Both flags
 * off = no-op, 0 (byte-identical cache).
 */
export function patchLogoRowsFromDetail(
  queryClient: QueryClient,
  detail: BaseLogo | null | undefined,
): number {
  if (!enviroment.productPendingEnabled && !enviroment.cloneInstantEnabled) {
    return 0;
  }
  if (!detail || typeof detail.id !== "number") return 0;
  if (isLogoDetailFromList(detail)) return 0;
  const scope = actAsCacheKey();
  const detailUpdatedAt =
    queryClient.getQueryState(logoDetailKey(detail.id))?.dataUpdatedAt ?? 0;
  let written = 0;
  queryClient
    .getQueryCache()
    .findAll({ queryKey: ["logos"] })
    .forEach((query) => {
      const key = query.queryKey;
      if (isLogoDetailQueryKey(key)) return;
      if (logoQueryKeyScope(key) !== scope) return;
      if (detailUpdatedAt > 0 && query.state.dataUpdatedAt > detailUpdatedAt) {
        return;
      }
      const current = query.state.data;
      if (current == null) return;
      const next = patchCachedLogoRowsData(current, detail);
      if (next === current) return;
      queryClient.setQueryData(key, next);
      written += 1;
    });
  return written;
}
